import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../auth/context";
import { BugReportModal } from "../components/BugReportModal";
import {
  fetchReports,
  patchReport,
  type Report,
  type ReportPriority,
} from "../reports/api";

// /reports — public-within-the-app feedback wall.  Top section is open
// items (most actionable), bottom is the changelog of what's been
// addressed and how.  The admin sees inline controls to mark a report
// addressed and add a resolution note; everyone else just reads.

export function Reports() {
  const { state } = useAuth();
  const accessToken =
    (state.status === "authenticated" && state.session.access_token) ||
    (state.status === "offline" && state.session.access_token) ||
    null;
  const user =
    (state.status === "authenticated" || state.status === "offline") &&
    state.user
      ? state.user
      : null;
  const isAdmin = !!user?.is_admin;

  const [items, setItems] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchReports(accessToken);
      setItems(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Scroll to the report named in the URL hash once the cards land.  The
  // browser's default hash-jump fires at navigation time, before our
  // async fetch resolves, so we re-attempt after `items` arrives.  Runs
  // once per items change; subsequent in-page hash clicks are handled by
  // the browser normally.
  useEffect(() => {
    if (items.length === 0) return;
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      el.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, [items]);

  const { open, addressed } = useMemo(() => {
    return {
      open: items.filter((r) => r.status === "open"),
      addressed: items.filter((r) => r.status === "addressed"),
    };
  }, [items]);

  if (state.status === "loading") {
    return <main className="p-6 text-sm text-slate-500 dark:text-slate-400">Loading…</main>;
  }
  if (state.status === "unauthenticated") {
    return <Navigate to="/signin" replace />;
  }

  return (
    <main className="mx-auto max-w-3xl p-4 sm:p-6">
      <header className="mb-6 flex flex-wrap items-baseline justify-between gap-y-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
            Bug &amp; feature reports
          </h1>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            What's been suggested, what's been fixed.
          </p>
        </div>
        <div className="flex items-baseline gap-3">
          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            className="rounded-md bg-slate-900 dark:bg-slate-100 px-3 py-1.5 text-sm font-medium text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200"
          >
            + New report
          </button>
          <Link
            to="/"
            className="text-sm text-slate-500 dark:text-slate-400 underline hover:text-slate-700 dark:hover:text-slate-200"
          >
            ← Home
          </Link>
        </div>
      </header>

      {composerOpen && accessToken && (
        <BugReportModal
          accessToken={accessToken}
          onSubmitted={() => void reload()}
          onClose={() => setComposerOpen(false)}
        />
      )}

      {error && (
        <div className="mb-4 rounded-md bg-rose-50 dark:bg-rose-950 px-3 py-2 text-sm text-rose-700 dark:text-rose-400">
          Couldn't load reports: {error}
        </div>
      )}

      <Section title={`Open (${open.length})`} empty="Nothing open. Nice.">
        {open.map((r) => (
          <ReportCard
            key={r.id}
            report={r}
            isAdmin={isAdmin}
            currentUserId={user?.id ?? null}
            accessToken={accessToken}
            onChanged={reload}
          />
        ))}
      </Section>

      <Section
        title={`Addressed (${addressed.length})`}
        empty="No reports addressed yet."
      >
        {addressed.map((r) => (
          <ReportCard
            key={r.id}
            report={r}
            isAdmin={isAdmin}
            currentUserId={user?.id ?? null}
            accessToken={accessToken}
            onChanged={reload}
          />
        ))}
      </Section>

      {loading && (
        <p className="mt-4 text-xs text-slate-400 dark:text-slate-500">Refreshing…</p>
      )}
    </main>
  );
}

function Section({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode;
}) {
  const items = Array.isArray(children) ? children : [children];
  const hasAny = items.some((c) => c);
  return (
    <section className="mb-8">
      <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {title}
      </h2>
      {hasAny ? (
        <ul className="space-y-3">{children}</ul>
      ) : (
        <p className="text-sm text-slate-400 dark:text-slate-500">{empty}</p>
      )}
    </section>
  );
}

const KIND_BADGE: Record<Report["kind"], string> = {
  bug: "bg-rose-100 dark:bg-rose-900 text-rose-700 dark:text-rose-400",
  feature: "bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300",
};

const PRIORITY_BADGE: Record<ReportPriority, string> = {
  high: "bg-rose-600 text-white",
  med: "bg-amber-500 text-white",
  low: "bg-slate-400 text-white",
};

function ReportCard({
  report,
  isAdmin,
  currentUserId,
  accessToken,
  onChanged,
}: {
  report: Report;
  isAdmin: boolean;
  currentUserId: string | null;
  accessToken: string | null;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [resolution, setResolution] = useState(report.resolution ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Reporter-edit panel state.  Hidden by default; toggled by the
  // "Edit" button below the body (visible only to the reporter and
  // only while the report is still open — closed reports are read-only).
  const [contentEditing, setContentEditing] = useState(false);
  const [draftKind, setDraftKind] = useState(report.kind);
  const [draftPriority, setDraftPriority] = useState(report.priority);
  const [draftTitle, setDraftTitle] = useState(report.title);
  const [draftBody, setDraftBody] = useState(report.body);
  const isReporter =
    !!currentUserId &&
    !!report.reporter?.id &&
    report.reporter.id === currentUserId;
  const canEditContent = isReporter && report.status === "open";

  const reporterLabel =
    report.reporter?.handle
      ? `@${report.reporter.handle}`
      : report.reporter?.displayName?.trim() ||
        report.reporter?.email?.split("@")[0] ||
        "anonymous";
  const resolverLabel =
    report.resolver?.handle
      ? `@${report.resolver.handle}`
      : report.resolver?.displayName?.trim() ||
        report.resolver?.email?.split("@")[0] ||
        null;

  async function markAddressed() {
    if (!accessToken) return;
    setBusy(true);
    setErr(null);
    try {
      await patchReport(accessToken, report.id, {
        status: "addressed",
        resolution: resolution.trim(),
      });
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function reopen() {
    if (!accessToken) return;
    if (!confirm("Re-open this report? Its resolution note will be cleared.")) return;
    setBusy(true);
    setErr(null);
    try {
      await patchReport(accessToken, report.id, { status: "open" });
      setResolution("");
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function saveContent() {
    if (!accessToken) return;
    const title = draftTitle.trim();
    if (!title) {
      setErr("Title can't be empty.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await patchReport(accessToken, report.id, {
        // Send only what changed — server COALESCEs nulls, but skipping
        // unchanged fields keeps the API call honest and the audit
        // story (next pull) clearer.
        ...(draftKind !== report.kind ? { kind: draftKind } : {}),
        ...(draftPriority !== report.priority
          ? { priority: draftPriority }
          : {}),
        ...(title !== report.title ? { title } : {}),
        ...(draftBody !== report.body ? { body: draftBody } : {}),
      });
      setContentEditing(false);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function cancelContentEdit() {
    setContentEditing(false);
    setDraftKind(report.kind);
    setDraftPriority(report.priority);
    setDraftTitle(report.title);
    setDraftBody(report.body);
    setErr(null);
  }

  return (
    <li
      id={report.id}
      className="scroll-mt-16 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-sm target:ring-2 target:ring-indigo-400"
    >
      {contentEditing ? (
        <ContentEditPanel
          kind={draftKind}
          onKindChange={setDraftKind}
          priority={draftPriority}
          onPriorityChange={setDraftPriority}
          title={draftTitle}
          onTitleChange={setDraftTitle}
          body={draftBody}
          onBodyChange={setDraftBody}
          busy={busy}
          error={err}
          onCancel={cancelContentEdit}
          onSave={() => void saveContent()}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-2">
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${KIND_BADGE[report.kind]}`}
            >
              {report.kind === "bug" ? "🐞 Bug" : "✨ Feature"}
            </span>
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${PRIORITY_BADGE[report.priority]}`}
            >
              {report.priority}
            </span>
            <h3 className="flex-1 text-sm font-medium text-slate-900 dark:text-slate-100">
              {report.title}
            </h3>
            <span className="text-[11px] text-slate-400 dark:text-slate-500" title={report.createdAt}>
              {formatDate(report.createdAt)}
            </span>
          </div>

          {report.body && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-200">
              {report.body}
            </p>
          )}

          <p className="mt-2 flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
            <span>
              Reported by{" "}
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {reporterLabel}
              </span>
            </span>
            {canEditContent && (
              <button
                type="button"
                onClick={() => setContentEditing(true)}
                className="text-[11px] text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-800 dark:hover:text-indigo-300"
              >
                Edit
              </button>
            )}
          </p>
        </>
      )}

      {report.status === "addressed" && (
        <div className="mt-3 rounded-md bg-emerald-50 dark:bg-emerald-950 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-200">
          <div className="text-[10px] font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
            Addressed
            {report.resolvedAt && (
              <span className="ml-1 normal-case text-emerald-600 dark:text-emerald-400">
                · {formatDate(report.resolvedAt)}
              </span>
            )}
            {resolverLabel && (
              <span className="ml-1 normal-case text-emerald-600 dark:text-emerald-400">
                · by {resolverLabel}
              </span>
            )}
          </div>
          {report.resolution ? (
            <p className="mt-1 whitespace-pre-wrap">{report.resolution}</p>
          ) : (
            <p className="mt-1 italic text-emerald-700 dark:text-emerald-300">
              (no resolution note)
            </p>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="mt-3 border-t border-slate-100 dark:border-slate-800 pt-2">
          {report.status === "open" ? (
            editing ? (
              <div className="flex flex-col gap-2">
                <textarea
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  rows={3}
                  maxLength={5000}
                  placeholder="What was done to address this? (optional)"
                  className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                />
                {err && (
                  <p className="text-xs text-rose-600 dark:text-rose-400">Couldn't save: {err}</p>
                )}
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setResolution(report.resolution ?? "");
                      setErr(null);
                    }}
                    className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={markAddressed}
                    disabled={busy}
                    className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {busy ? "Saving…" : "Mark addressed"}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs text-emerald-700 dark:text-emerald-300 underline hover:text-emerald-900 dark:hover:text-emerald-200"
              >
                Mark addressed…
              </button>
            )
          ) : (
            <div className="flex flex-col gap-1">
              {err && (
                <p className="text-xs text-rose-600 dark:text-rose-400">Couldn't re-open: {err}</p>
              )}
              <button
                type="button"
                onClick={reopen}
                disabled={busy}
                className="self-start text-xs text-slate-500 dark:text-slate-400 underline hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-50"
              >
                {busy ? "Re-opening…" : "Re-open"}
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

// Inline panel that swaps in for a report's badges/title/body when the
// reporter clicks "Edit".  Shape mirrors BugReportModal (kind / priority
// segmented buttons + title input + details textarea) so editing feels
// like the same form they used to submit.
function ContentEditPanel({
  kind,
  onKindChange,
  priority,
  onPriorityChange,
  title,
  onTitleChange,
  body,
  onBodyChange,
  busy,
  error,
  onCancel,
  onSave,
}: {
  kind: Report["kind"];
  onKindChange: (k: Report["kind"]) => void;
  priority: ReportPriority;
  onPriorityChange: (p: ReportPriority) => void;
  title: string;
  onTitleChange: (s: string) => void;
  body: string;
  onBodyChange: (s: string) => void;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: () => void;
}) {
  return (
    <div className="flex flex-col gap-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex overflow-hidden rounded-md border border-slate-300 dark:border-slate-600">
          <SegBtn active={kind === "bug"} onClick={() => onKindChange("bug")}>
            🐞 Bug
          </SegBtn>
          <SegBtn
            active={kind === "feature"}
            onClick={() => onKindChange("feature")}
          >
            ✨ Feature
          </SegBtn>
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-slate-300 dark:border-slate-600">
          <SegBtn
            active={priority === "high"}
            activeCls="bg-rose-600 text-white"
            onClick={() => onPriorityChange("high")}
          >
            High
          </SegBtn>
          <SegBtn
            active={priority === "med"}
            activeCls="bg-amber-500 text-white"
            onClick={() => onPriorityChange("med")}
          >
            Med
          </SegBtn>
          <SegBtn
            active={priority === "low"}
            activeCls="bg-slate-500 text-white"
            onClick={() => onPriorityChange("low")}
          >
            Low
          </SegBtn>
        </div>
      </div>
      <input
        value={title}
        onChange={(e) => onTitleChange(e.target.value)}
        maxLength={200}
        placeholder="Title"
        className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
      />
      <textarea
        value={body}
        onChange={(e) => onBodyChange(e.target.value)}
        rows={4}
        maxLength={5000}
        placeholder="Details (optional)"
        className="resize-y rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
      />
      {error && (
        <p className="text-xs text-rose-600">Couldn't save: {error}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={busy || !title.trim()}
          className="rounded-md bg-slate-900 dark:bg-slate-100 px-2 py-1 text-xs font-medium text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200 disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function SegBtn({
  active,
  activeCls = "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900",
  onClick,
  children,
}: {
  active: boolean;
  activeCls?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`px-3 py-1 text-xs ${
        active ? activeCls : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
